import {useState, useMemo} from "react";
import {BOOKING_CONSTRAINTS} from "@/constants/booking";
import {usePolicyCatalog} from "@/hooks/usePolicyCatalog";
import {buildPolicyParams} from "@/utils/policyParams";
import {resolveNoShow, noShowFromPolicy, noShowTierRow} from "@/utils/noShowPolicy";
import {round1} from "@/utils/policyFormat";
import {img} from "@/utils/pluginImg.js";
import {HopnReviewsWidget} from "@/customComponents/BookRideSteps/Components/hopnReviewsWidget.jsx";

/**
 * "Passenger Late or No Show" / "Driver Running Late" — the two lateness policies
 * that used to be separate pages (PassengerPoliciesContent / DriverPoliciesContent)
 * with identical copy, now ONE component behind the same sliding tab bar as the
 * Cancellation / Modification / Refund popup.
 *
 * It IS a `.hopn-cmr` (tab bar, tier rows and grey notes are all that component's
 * classes) with a two-tab variant plus a few lateness-only blocks.
 *
 * Numbers come from the caller's `policy` object — the same RAW shape the rest of
 * policiesContent.jsx takes, so the booking flow feeds it bookingPolicyValues() and
 * the trip pages tripPolicyValues(trip.policy_popup). Copy that names a duration
 * says "{n} min", so a config change rewrites the sentence too.
 */
const TABS = [
    {key: 'passenger', label: 'Passenger Late or No Show', icon: 'person-icon.svg'},
    {key: 'driver', label: 'Driver Running Late', icon: 'clock-bold.svg'},
];

/*
 * The one-line promise under the tabs, inside the mint hero. Per TAB, because the hero is
 * always on screen and a passenger-only sentence would be describing the wrong policy half
 * the time.
 */
const TAB_MESSAGE = {
    passenger: 'Please be ready at your scheduled pickup time to avoid waiting fees.',
    driver: "We'll keep you updated in real time if your driver is running late.",
};

/**
 * One priced tier: a black chip naming it, then a grey row — the amount in its own lighter
 * block on the left, what it buys on the right.
 *
 * Replaces the old flat `TierRow`, which set the amount as bare text on the modal's white
 * with only a hairline between tiers. At a glance the three amounts read as one list of
 * numbers rather than three separate rules, which is the whole thing this panel exists to
 * explain. MIRRORS the plugin's copy — change one, change the other.
 */
function Tier({chip, value, unit, unitBelow, title, qualifier, subtitle}) {
    return (
        <div className="hopn-lateness-tier">
            <span className="hopn-lateness-chip">{chip}</span>
            <div className="hopn-lateness-tier-row">
                <span className={`hopn-lateness-tier-amount${unitBelow ? ' is-stacked' : ''}`}>
                    {value}{unit && <small>{unit}</small>}
                </span>
                <div className="hopn-lateness-tier-text">
                    <h5>{title}{qualifier && <span> {qualifier}</span>}</h5>
                    {subtitle && <p>{subtitle}</p>}
                </div>
            </div>
        </div>
    );
}

// Grey rounded callout — the CMR note, optionally without a head (the plain
// definition cards sitting beside a titled one).
function Note({icon, title, children}) {
    return (
        <div className="hopn-cmr-note">
            {title && (
                <div className="hopn-cmr-note-head">
                    {icon && <img src={icon} alt=""/>}
                    <h6>{title}</h6>
                </div>
            )}
            {children}
        </div>
    );
}

export function LatenessPoliciesContent({initialTab = 'passenger', policy = {}, spacing = true}) {
    const [tab, setTab] = useState(initialTab);

    // Pickup-leg waiting policy, clamped to 1 decimal so raw seconds never show as
    // long fractions.
    const freeMinutes = round1((policy.waitingPickupFreeSeconds ?? 0) / 60);
    const perMinuteDollars = round1((policy.waitingPickupRateCentsPerSecond ?? 0) * 60 / 100);
    const maxWaitMinutes = round1((policy.waitingPickupMaxWaitSeconds ?? 0) / 60);
    const supportPhone = BOOKING_CONSTRAINTS.CONTACT_NUMBER;

    // The no-show CONSEQUENCE. Two sources, in order of how well they know this trip:
    //   1. the rule on the caller's own policy payload (a trip's, or calculate-policies');
    //   2. the /config/policies catalog, matched to the trip's — or the in-progress
    //      booking's — direction + distance.
    // Both absent (or the catalog can't answer without contradicting itself) → null, and
    // the row below keeps the generic "Full fare may apply." wording it always had.
    const {data: catalog} = usePolicyCatalog();
    const noShowRow = useMemo(
        () => noShowTierRow(
            noShowFromPolicy({no_show: policy.noShow})
            || resolveNoShow(catalog, policy.noShowParams || buildPolicyParams())
        ),
        [catalog, policy.noShow, policy.noShowParams]
    );

    return (
        <div className={`hopn-cmr hopn-lateness${spacing ? ' modalPages-spacing pb-8' : ''}`}>
            {/* Mint HERO: the tab bar and the one-line promise share one surface, with a
                tail pointing at the tiers below — so the sentence reads as this panel
                speaking, not as a stray banner. The tab bar itself is still the CMR's
                sliding-pill component; only what sits behind it changed. */}
            {/* The mint shape is an IMAGE (Subtract.png from the design), not CSS: its
                bottom edge steps up on the right with a CONCAVE corner at the junction,
                which no border-radius can draw. Stretched to the block's own size, so the
                message can run to two lines without the art having to be re-cut. Set
                inline because the URL differs per portal (img() here, assetUrl in the
                plugin) and the shared SCSS can't know either path. */}
            <div
                className="hopn-lateness-hero"
                style={{backgroundImage: `url(${img('lateness-hero-bg.png')})`}}
            >
                <div className="hopn-cmr-tabs hopn-cmr-tabs--two" role="tablist">
                    <span
                        className="hopn-cmr-tab-indicator"
                        style={{transform: `translateX(${TABS.findIndex((t) => t.key === tab) * 100}%)`}}
                        aria-hidden="true"
                    />
                    {TABS.map((t) => (
                        <button
                            key={t.key}
                            type="button"
                            role="tab"
                            aria-selected={tab === t.key}
                            className={`hopn-cmr-tab ${tab === t.key ? 'active' : ''}`}
                            onClick={() => setTab(t.key)}
                        >
                            <img src={img(t.icon)} alt=""/>
                            {t.label}
                        </button>
                    ))}
                </div>
                <p className="hopn-lateness-hero-msg">{TAB_MESSAGE[tab]}</p>
            </div>

            <div className="hopn-cmr-panel">
                {tab === 'passenger' && (
                    <>
                        {/* The three tiers, in the order they happen. Every number is
                            admin-configured (see the reads above), so a policy change
                            rewrites these rows rather than dating them. */}
                        <div className="hopn-lateness-tiers">
                            <Tier
                                chip="Late Seating"
                                value="$0"
                                title={`Free ${freeMinutes} min waiting & seating`}
                                subtitle="Luggage assistance included"
                            />

                            {/* The two waiting tiers are alternatives, not a sequence of
                                charges — the OR says so between them. */}
                            <div className="hopn-lateness-or"><span>OR</span></div>

                            <Tier
                                chip="Late Waiting fee applies"
                                value={`$${perMinuteDollars}`}
                                unit="/min"
                                title={`After ${freeMinutes} min`}
                                qualifier={`(max ${maxWaitMinutes} min waiting)`}
                                subtitle="Otherwise driver schedule & discretion"
                            />

                            {/* Value + sentence are the ADMIN'S rule (kept %, or a flat fee,
                                and the backend's own wording) — see utils/noShowPolicy. The
                                literals are the fallback for a trip we can't match a rule to,
                                and are the wording this row carried before rules existed. */}
                            <Tier
                                chip="CONSIDERED A NO-SHOW"
                                value={noShowRow?.value ?? "Full"}
                                // "(May Apply)" is appended to WHATEVER unit comes back, not just
                                // the fallback — a resolved admin rule returns a bare "fare", so
                                // hedging only the fallback meant the qualifier never showed on a
                                // real trip. JSX, not a string, so "fare" stays bold and the
                                // qualifier beside it does not (see .is-stacked small in SCSS).
                                unit={<><b>{noShowRow?.unit ?? "fare"}</b> (May Apply)</>}
                                unitBelow={true}
                                title={`After ${maxWaitMinutes} min waiting`}
                                subtitle={noShowRow?.subtitle || "Full fare may apply."}
                            />
                            {/* Two cards side by side: why the fee exists, and what actually
                                counts as a no-show. */}
                            <div className="hopn-lateness-cards">
                                <Note icon={img("info-icon-grey.svg")} title="Why late fee & no show charges?">
                                    <p className="mb-0!">This helps us maintain our guaranteed pickup service for all passengers.</p>
                                </Note>
                                {/* Same head as the card beside it — the design pairs two
                                    identically titled cards, one answering WHY the fee
                                    exists and one WHAT counts as a no-show. Headless, this
                                    one read as an orphaned paragraph. */}
                                <Note icon={img("info-icon-grey.svg")} title="Why late fee & no show charges?">
                                    <p className="mb-0!">A Driver No-Show occurs when the assigned driver fails to arrive at the
                                        scheduled pickup location, does not contact the passenger.</p>
                                </Note>
                            </div>
                        </div>
                    </>
                )}

                {tab === 'driver' && (
                    /* The whole tab is ONE lifted surface — same card the passenger tab's
                       tiers sit on — rather than blocks floating on the modal. */
                    <div className="hopn-lateness-section">
                        {/* LIVE-TRACKING hero: the headline, the promise and the proof strip
                            share ONE grey card, with the app screenshot alongside them. The
                            three parts used to be stacked as separate blocks (trust strip,
                            then a copy/phone split, then store badges); the design reads them
                            as a single claim — "we track it, and here's the record" — so they
                            sit on one surface. */}
                        <div className="hopn-lateness-live">
                            <div className="hopn-lateness-live-text">
                                <h6 className="hopn-lateness-label">Live tracking</h6>
                                <h3>Driver Running Late?</h3>
                                <p className="hopn-lateness-live-lead">We'll keep you informed with live updates.</p>
                                <p className="mb-0!">If your driver is delayed, you'll receive notifications automatically.</p>

                                {/* Same three proof points as the booking flow's trust strip. */}
                                <div className="hopn-lateness-trust">
                                    {/* Caption sits UNDER THE TITLE, clear of the icon. Only the
                                        rides cell hangs a second caption across the whole column
                                        (…-cell-inner) — that is the one line the design runs back
                                        to the icon's left edge. */}
                                    <div className="hopn-lateness-trust-cell">
                                        <img src={img("slider-shild.svg")} alt=""/>
                                        <div>
                                            <strong>0 Missed pickups</strong>
                                            <small>Last 12 months</small>
                                        </div>
                                    </div>
                                    <div className="hopn-lateness-trust-cell hopn-lateness-rating">
                                        {/* Google wordmark on top, the LIVE Elfsight rating
                                            (score + stars + count) beneath it. */}
                                        <img className="hopn-lateness-rating-mark" src={img("google-wordmark.svg")} alt="Google"/>
                                        <HopnReviewsWidget layout="summary"/>
                                    </div>
                                    <div className="hopn-lateness-trust-cell-inner">
                                        <div className="hopn-lateness-trust-cell">
                                            <img src={img("persons-icons.svg")} alt=""/>
                                            <div>
                                                <strong>10,000+</strong>
                                                <small>Rides completed</small>
                                            </div>
                                        </div>
                                        <small>Over 18 Years In Business</small>
                                    </div>
                                </div>
                            </div>

                            <img className="hopn-lateness-phone" src={img("live-tracking-phone.png")} alt=""/>
                        </div>

                        {/* The guarantee is the loudest thing on the tab in the design — a
                            display heading, not the small eyebrow this used to be. */}
                        <h3 className="hopn-lateness-guarantee">
                            <img src={img("good-shild.svg")} alt=""/>
                            Guaranteed pickup
                        </h3>
                        <p className="hopn-lateness-bar">
                            <strong>We've Got You Covered</strong> <span>(Book With Confidence)</span>
                        </p>
                        <p className="hopn-lateness-mint mb-0!">Your pickup is our priority. If your driver has an
                            emergency or unforeseen circumstances, we'll immediately work to assign another
                            qualified driver.</p>

                        {/* Both cards carry the SAME question, as the design does — one
                            answers it with what we do about a driver who can't make it,
                            the other with what counts as the driver not showing at all. */}
                        <div className="hopn-lateness-cards">
                            <Note icon={img("info-icon-grey.svg")} title="Why late fee & no show charges?">
                                <p className="mb-0!">Our goal is to provide every passenger with guaranteed pickup. If a driver
                                    experiences an emergency or cannot complete the trip, we'll assign another
                                    qualified driver.</p>
                            </Note>
                            <Note icon={img("info-icon-grey.svg")} title="Why late fee & no show charges?">
                                <p className="mb-0!">A Driver No-Show occurs when the assigned driver fails to arrive at the
                                    scheduled pickup location, does not contact the passenger.</p>
                            </Note>
                        </div>

                        {supportPhone && (
                            <div className="hopn-lateness-help">
                                <h5>Need help?</h5>
                                <p className="mb-0!">
                                    <span>We're here 24/7</span>
                                    <a href={`tel:${String(supportPhone).replace(/[^0-9+]/g, '')}`}>
                                        {/* call-outline.svg, NOT call.svg: this portal already
                                            has its own call.svg — a 37px mint-FILLED glyph used
                                            by ChatSupport — so the plugin's 16px dark outline
                                            handset (what this row is designed around) had to
                                            come in under a different name rather than replace
                                            it. Same artwork as the plugin's call.svg. */}
                                        <img src={img("call-outline.svg")} alt=""/>
                                        {supportPhone}
                                    </a>
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default LatenessPoliciesContent;
